import { ExclamationCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Space, Spin, Tooltip, Typography, message, notification } from 'antd';
import moment, { Moment } from 'moment';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useIsFetching, useQueryClient } from 'react-query';
import 'react-quill-new/dist/quill.snow.css';
import { useSearchParams } from 'react-router-dom';

import Card from '../../../Card';
import useAPIQuery from '../../../api/useAPIQuery';
import ErrorResult from '../../../components/ErrorResult';
import Form from '../../../components/Form';
import PageContent from '../../../components/PageContent';
import { quillGetHTMLString } from '../../../components/RichEditor/RichEditor';
import useDiscardChangesModal from '../../../hooks/useDiscardChangesModal';
import usePublicationMutation from '../../../hooks/usePublicationMutation';
import useTypedNavigate from '../../../hooks/useTypedNavigate';
import useTypedParams from '../../../hooks/useTypedParams';
import { withRouteChange } from '../../../services/APM';
import { useGenerateOpportunityDescriptionStatus } from '../../../sinapse/generate_opportunity_description';
import { JobPostingDetail, JobPostingId } from '../../../types';
import { isJobPostingDescriptionLengthValid } from '../../../validators/jobPostingDescriptionValidator';
import CareersPageForm from './CareersPageForm';
import JobBoardsCards from './JobBoardsCards';
import PublicationInfoForm from './PublicationInfoForm';
import UnlistedForm from './UnlistedForm';

export interface PublicationFormValues {
  externalPublication: boolean;
  externalPublicationPeriod: [Moment, Moment] | null;
  timezone: string;
  description: string;
  workModel?: JobPostingDetail['work_model'];
  hireModel?: JobPostingDetail['hire_model'];
  country: string;
  state: string;
  city: string;
  googleForJobsExternalPublication: boolean;
  netvagasExternalPublication: boolean;
  linkedinExternalPublication: boolean;
  unlistedExternalPublication: boolean;
  careerjetExternalPublication: boolean;
  joobleExternalPublication: boolean;
  competencies?: number[];
  whatsAppSubscription: boolean;
}

const PublicationForm = () => {
  const { t } = useTranslation();

  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });
  const navigate = useTypedNavigate();
  const [form] = Form.useForm<PublicationFormValues>();
  const [leavePage, setLeavePage] = useState<boolean>(false);
  const [setIsDirty] = useDiscardChangesModal(form);
  const [searchParams] = useSearchParams();
  const generateOpportunityDescriptionStatus = useGenerateOpportunityDescriptionStatus(id);

  const queryClient = useQueryClient();

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const mutation = usePublicationMutation({
    onSuccess: () => {
      setIsDirty(false);
      message.success(t('Vaga atualizada'));

      if (leavePage) {
        navigate({
          pathname: '/:tenant/opportunities/:id/',
          params: {
            tenant,
            id,
          },
        });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries(`/job-postings/${id}/`);
    },
    onError: (e, variables, context) => {
      form.resetFields([
        'externalPublication',
        'linkedinExternalPublication',
        'netvagasExternalPublication',
        'googleForJobsExternalPublication',
        'careerjetExternalPublication',
        'joobleExternalPublication',
      ]);
      return queryClient.defaultMutationOptions().onError?.(e, variables, context);
    },
  });

  const showConfirm = (externalPublication: boolean, onOk: () => void, onCancel: () => void) => {
    if (externalPublication) {
      Modal.confirm({
        title: t('Publicar vaga'),
        icon: <ExclamationCircleOutlined />,
        content: (
          <Typography.Text>
            <Trans>Salvar as alterações vai</Trans>{' '}
            <Typography.Text strong>
              <Trans>publicar</Trans>
            </Typography.Text>{' '}
            <Trans>a vaga na página de carreiras. Tem certeza que deseja continuar?</Trans>
          </Typography.Text>
        ),
        onOk,
        onCancel,
      });
    } else {
      Modal.confirm({
        title: t('Parar publicação'),
        icon: <ExclamationCircleOutlined />,
        content: (
          <Typography.Text>
            <Trans>Salvar as alterações vai</Trans>{' '}
            <Typography.Text strong>
              <Trans>remover</Trans>
            </Typography.Text>{' '}
            <Trans>a vaga na página de carreiras. Tem certeza que deseja continuar?</Trans>
          </Typography.Text>
        ),
        onOk,
        onCancel,
      });
    }
  };

  const country = Form.useWatch('country', form);
  const state = Form.useWatch('state', form);

  const isFething = useIsFetching([
    '/configs/tenant-configurations/',
    `/country-state-city/countries/${country?.toUpperCase()}/states/`,
    `/country-state-city/countries/${country?.toUpperCase()}/states/${state}/cities/`,
  ]);

  const unlistedExternalPublication = jobPosting.data?.unlisted_external_publication;

  const netvagasExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.netvagas_external_publication;

  const googleForJobsExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.google_for_jobs_external_publication;

  const linkedinExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.linkedin_external_publication;

  const careerjetExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.careerjet_external_publication;

  const joobleExternalPublication =
    jobPosting.data?.external_publication && jobPosting.data.jooble_external_publication;

  return (
    <ErrorResult error={jobPosting.error}>
      <Helmet>
        <title>
          {t('Publicação')} | {jobPosting.data?.name || '...'} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <PageContent>
        <Spin spinning={jobPosting.isLoading || isFething > 0 || config.isLoading}>
          <Form<PublicationFormValues>
            form={form}
            layout="vertical"
            validateMessages={{
              required: t('Campo obrigatório.'),
            }}
            initialValues={{
              timezone: jobPosting.data?.timezone || 'America/Sao_Paulo',
              description: quillGetHTMLString(jobPosting.data?.description || '') || '',
              competencies: jobPosting.data?.competencies || [],
              externalPublicationPeriod:
                jobPosting.data?.external_publication_start_at &&
                jobPosting.data?.external_publication_end_at
                  ? [
                      moment(jobPosting.data?.external_publication_start_at).utc(),
                      moment(jobPosting.data?.external_publication_end_at).utc(),
                    ]
                  : null,
              externalPublication: jobPosting.data?.external_publication || false,
              workModel: jobPosting.data?.work_model,
              hireModel: jobPosting.data?.hire_model,
              country:
                !jobPosting.data?.country && searchParams.get('new')
                  ? 'BR'
                  : jobPosting.data?.country,
              state: jobPosting.data?.state === '' ? undefined : jobPosting.data?.state,
              city: jobPosting.data?.city === '' ? undefined : jobPosting.data?.city,
              googleForJobsExternalPublication,
              netvagasExternalPublication,
              linkedinExternalPublication,
              unlistedExternalPublication,
              careerjetExternalPublication,
              joobleExternalPublication,
              whatsAppSubscription: jobPosting.data?.whatsapp_subscription,
            }}
            onFinish={(variables) => {
              if (variables.externalPublication !== !!jobPosting.data?.external_publication) {
                showConfirm(
                  variables.externalPublication,
                  () => {
                    mutation.mutate({
                      ...variables,
                      jobPostingId: id,
                      workModel: variables.workModel,
                      hireModel: variables.hireModel,
                    });
                  },
                  () => {
                    form.setFieldsValue({
                      externalPublication: jobPosting.data?.external_publication,
                      linkedinExternalPublication: !!linkedinExternalPublication,
                      netvagasExternalPublication: !!netvagasExternalPublication,
                      googleForJobsExternalPublication: !!googleForJobsExternalPublication,
                      unlistedExternalPublication: !!unlistedExternalPublication,
                      careerjetExternalPublication: !!careerjetExternalPublication,
                      joobleExternalPublication: !!joobleExternalPublication,
                    });
                  }
                );
              } else {
                mutation.mutate({
                  ...variables,
                  jobPostingId: id,
                  workModel: variables.workModel,
                  hireModel: variables.hireModel,
                });
              }
            }}
            onValuesChange={(changedValues, values) => {
              setIsDirty(form.isFieldsTouched());

              if (
                (changedValues.externalPublication ||
                  changedValues.careerjetExternalPublication ||
                  changedValues.joobleExternalPublication ||
                  changedValues.linkedinExternalPublication ||
                  changedValues.unlistedExternalPublication ||
                  changedValues.netvagasExternalPublication ||
                  changedValues.googleForJobsExternalPublication) &&
                !isJobPostingDescriptionLengthValid(jobPosting.data?.description)
              ) {
                notification.warn({
                  message: t('Para publicar a vaga preencha a descrição da vaga'),
                });
              }
            }}
          >
            <Space direction="vertical" size={16}>
              <Card>
                <Form.Legend>
                  <Trans>Informações para Publicação</Trans>{' '}
                  <Tooltip title={t('Informações que aparecem para o candidato')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Form.Legend>
                <PublicationInfoForm
                  linkedinExternalPublication={
                    jobPosting.data?.linkedin_external_publication ?? false
                  }
                  externalPublication={jobPosting.data?.external_publication ?? false}
                  showWhatsAppSubscription={config.data?.t008_enableWhatsAppSubscription}
                />
              </Card>
              <Form.Row>
                <Form.Col span={24}>
                  <Form.Legend>
                    <Trans>Escolha como divulgar sua vaga:</Trans>
                  </Form.Legend>
                </Form.Col>
                <Form.Col span={12}>
                  <CareersPageForm
                    loading={mutation.isLoading}
                    disabled={generateOpportunityDescriptionStatus === 'pending'}
                  />
                </Form.Col>
                <Form.Col span={12}>
                  <UnlistedForm
                    loading={mutation.isLoading}
                    disabled={generateOpportunityDescriptionStatus === 'pending'}
                  />
                </Form.Col>
              </Form.Row>
              <Form.Row>
                <Form.Col span={24}>
                  <Form.Legend>
                    <Trans>Divulgue sua vaga nos Job Boards abaixo:</Trans>
                  </Form.Legend>
                </Form.Col>
                <Form.Col>
                  <JobBoardsCards
                    loading={mutation.isLoading}
                    disabled={generateOpportunityDescriptionStatus === 'pending'}
                  />
                </Form.Col>
              </Form.Row>
              <Form.Footer>
                <Form.FooterEnd>
                  <Button
                    onClick={() =>
                      navigate({
                        pathname: '/:tenant/opportunities/:id/',
                        params: {
                          tenant,
                          id,
                        },
                      })
                    }
                  >
                    <Trans>Cancelar</Trans>
                  </Button>
                  {searchParams.get('new') ? (
                    <>
                      <Button
                        loading={mutation.isLoading}
                        disabled={generateOpportunityDescriptionStatus === 'pending'}
                        htmlType="submit"
                      >
                        <Trans>Salvar</Trans>
                      </Button>
                      <Button
                        htmlType="submit"
                        loading={mutation.isLoading}
                        disabled={generateOpportunityDescriptionStatus === 'pending'}
                        type="primary"
                        onClick={() => setLeavePage(true)}
                      >
                        <Trans>Salvar e Sair</Trans>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        htmlType="submit"
                        loading={mutation.isLoading}
                        disabled={generateOpportunityDescriptionStatus === 'pending'}
                        onClick={() => setLeavePage(true)}
                      >
                        <Trans>Salvar e Sair</Trans>
                      </Button>
                      <Button
                        loading={mutation.isLoading}
                        disabled={generateOpportunityDescriptionStatus === 'pending'}
                        htmlType="submit"
                        type="primary"
                      >
                        <Trans>Salvar</Trans>
                      </Button>
                    </>
                  )}
                </Form.FooterEnd>
              </Form.Footer>
            </Space>
          </Form>
          <div style={{ height: 80 }} />
        </Spin>
      </PageContent>
    </ErrorResult>
  );
};

export default withRouteChange()(PublicationForm);
